import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type {LayerInfo} from "@esWebAPI/es/model/Layer";

/** @class es.module.[resource]
 * Object providing [resource].
 */
export default  class LayerInfoModule {

    eventChannel: IEventChannel;
    readonly rqBroker: AjaxRequestBroker;
    readonly cache: CacheModule;
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.cache = cache;

        this.eventChannel.addListener("onDestroy", () => {
            this.eventChannel = null;
        })
    }

    private getLayerInfo(id: number, includeAttributeRanges: string[] | null | undefined, cacheKey: string, baseUrl: string, opts?: ApiOpts): JQueryPromise<LayerInfo> {
        let url = baseUrl;
        if (includeAttributeRanges) {
            url += '?includeAttributeRanges=' + es.util.url.stringify(includeAttributeRanges);
        }

        return this.cache.doGet(this.rqBroker, cacheKey, url, id, opts).then((rs: LayerInfo) => {
            // Cache doesn't take into account the URL, so we could get an old result from cache that doesn't have the requested attribute ranges
            // so, we need to check if all requested ranges are there, and if not, request again with noCache

            opts = opts || {};

            if (opts.noCache || !includeAttributeRanges) {
                return rs;
            }

            const attributeRanges = rs.featureInfo.attributeRanges;
            if (es.util.array.includesAll(Object.keys(attributeRanges), includeAttributeRanges)) {
                return rs;
            }

            opts.noCache = true;
            return this.cache.doGet(this.rqBroker, cacheKey, url, id, opts);
        }).done((rs: LayerInfo) => {
            if (this.eventChannel) { // destroy could have been called in the mean time...
                this.eventChannel.fireEvent("onGetLayerInfo", rs);
            }
        });
    }

    /**
     * @function es.module.layerInfo.getForLayerView
     * Get the resource from the server
     * @param id layerView id
     * @param includeAttributeRanges
     * @param opts
     */
    getForLayerView(id: number, includeAttributeRanges?: string[] | null, opts?: ApiOpts): JQueryPromise<LayerInfo>{
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL("LayerInfo") + "/layerview/" + id;
        return this.getLayerInfo(id, includeAttributeRanges, "LayerInfo_LayerView", url, opts);
    }

    /**
     * @function es.module.layerInfo.getFromLayerMeta
     * Get the resource from the server
     * @param id layerMeta id
     * @param includeAttributeRanges
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    getForLayerMeta(id: number, includeAttributeRanges?: string[], opts?: ApiOpts): JQueryPromise<LayerInfo>{
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL("LayerInfo") + "/layermeta/" + id;
        return this.getLayerInfo(id, includeAttributeRanges, "LayerInfo_LayerMeta", url, opts);
    }

    /**
     * @function es.module.layerInfo.getForGeoMap
     * Get the resource from the server
     * @param id geoMapid id
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    getForGeoMap(id: number, opts?: ApiOpts): JQueryPromise<LayerInfo> {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL("LayerInfo") + "/geomap/" + id;
        return this.rqBroker.doGet( {url: url, opts: opts} );
    }

    /**
     * @function es.module.layerInfo.getAll
     * Get the resource from the server
     * @param layerInfos array of objects with either a layerViewId or layerMetaId
     * @param opts
     * @return a jquery deferred object resolved when the request has completed:w
     */
    getAll(layerInfos: {layerMetaId?: number, layerViewId?:number}[], opts?: ApiOpts): JQueryPromise<LayerInfo[]>{
        if (!ValidateModule.checkMandatoryArg(layerInfos)) {
            return es.util.deferred.rejected();
        }

        let dfds: null | JQuery.Deferred<LayerInfo>[] = [];
        for(let i = 0; i < layerInfos.length; i++){
            const layerInfo = layerInfos[i];
            let result;
            if(layerInfo.layerViewId){
                result = this.cache.get("LayerInfo_LayerView", layerInfo.layerViewId);
            }else{
                result = this.cache.get("LayerInfo_LayerMeta", layerInfo.layerMetaId);
            }
            if(result){
                dfds.push(result);
            }else{
                dfds = null;
                break;
            }
        }

        if (dfds) {
            // All items are in cache
            return $.when.apply($, dfds).then(function() {
                // need an array of items...
                const ret: LayerInfo[] = [];
                ret.push.apply(ret, arguments as unknown as LayerInfo[]);

                return ret;
            }) as JQuery.Deferred<LayerInfo[]>;
        }

        const url = es.api.getURL("LayerInfo") + "/getall?layers=" + es.util.url.stringify(layerInfos);

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        }).done((layerInfos: LayerInfo[]) => {
            for(let i = layerInfos.length; i-- > 0;){
                const layerInfo = layerInfos[i];
                if(layerInfo.layerViewId > 0){
                    this.cache.set("LayerInfo_LayerView", layerInfo.layerViewId, layerInfo);
                }else{
                    this.cache.set("LayerInfo_LayerMeta", layerInfo.layerMetaId, layerInfo);
                }
            }
        });
    }
}